import React from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import PhoneIphoneIcon from '@material-ui/icons/PhoneIphone';
import CloudOffIcon from '@material-ui/icons/CloudOff';
import withShipment from '../../withShipment';
import { userInfoSelector } from '../../redux/selectors/auth';
import { bonusPhotoQueueSelector } from '../../redux/selectors/settings';
import {
  drainBonusPhotos,
  refreshBonusPhotoQueue,
} from '../../redux/actions/settings';
import { pendingPhotoStatus, writeOffUnproducible } from './pendingPhotos';
import './PendingPhotoGate.css';

// Stands between an employee and the end of their session — clocking out or
// logging out — while a bonus photo has not reached the server.
//
// The awkward part this has to handle: the photo and the clock-out are on
// DIFFERENT devices. Photos are taken on a phone and wait in that phone's
// outbox; clocking out happens on the shop computer. So the count comes from
// the server (see pendingPhotos.js), not from whatever browser this happens to
// be — a gate that read the local outbox would never fire on the computer,
// which is the only screen that matters for clocking out.
//
// An employee cannot CHOOSE to abandon a photo — the photo is the audit record
// for money that already left the drawer, and "I gave up" is not an outcome the
// business accepts. There is no skip button.
//
// But a photo whose bytes are not in any outbox is not a choice, it is a fact:
// nothing can produce it, and blocking on it only traps someone over something
// nobody can supply. So the gate writes those off automatically — after giving
// the outbox a chance to upload — records the loss on the bonus and in the log,
// and lets the employee through. What is refused is skipping a photo that could
// still be sent; what is released is one that provably cannot.
const POLL_MS = 4000;

// How long the outbox gets to upload before anything is written off. A photo
// mid-upload is not a missing one.
const DRAIN_GRACE_MS = 6000;

class PendingPhotoGate extends React.Component {
  state = { status: null, checking: false };

  // Ids already written off this session, so a poll cannot report one twice.
  writtenOff = new Set();

  drained = false;

  componentDidUpdate(prevProps) {
    if (!prevProps.open && this.props.open) {
      // Opening the gate is the employee asking for this to finish, so push
      // this device's outbox straight away rather than waiting for the drain.
      this.props.drainBonusPhotos(true);
      this.refresh();
      // Give that drain a moment to land before anything is judged
      // unproducible — a photo mid-upload is not a missing one.
      this.drained = false;
      this.drainGrace = setTimeout(() => {
        this.drained = true;
      }, DRAIN_GRACE_MS);
      this.timer = setInterval(this.refresh, POLL_MS);
    }
    if (prevProps.open && !this.props.open) this.stop();
  }

  componentWillUnmount() {
    this.stop();
  }

  stop() {
    clearInterval(this.timer);
    clearTimeout(this.drainGrace);
    this.timer = null;
    this.drained = false;
  }

  refresh = async () => {
    const status = await pendingPhotoStatus(this.props.user);
    if (this.timer || this.props.open) this.setState({ status });

    // Anything the server still wants that this device is not holding cannot
    // be produced by waiting. Record it as missing so the employee is not held
    // on the clock over a photo nobody can supply.
    //
    // Only after a drain has run (see componentDidUpdate) — otherwise this
    // could write off a photo that was seconds from uploading. And only once:
    // the ids leave the list as soon as the server stamps them.
    if (this.drained && status.unproducible && status.unproducible.length) {
      const ids = status.unproducible.filter((id) => !this.writtenOff.has(id));
      if (ids.length) {
        ids.forEach((id) => this.writtenOff.add(id));
        await writeOffUnproducible(ids);
        const after = await pendingPhotoStatus(this.props.user);
        if (this.timer || this.props.open) this.setState({ status: after });
        this.props.refreshBonusPhotoQueue();
      }
    }
  };

  handleRetry = () => {
    this.props.drainBonusPhotos(true);
    this.setState({ checking: true });
    setTimeout(() => this.setState({ checking: false }, this.refresh), 1200);
  };

  renderClear() {
    const { actionLabel, onProceed, onCancel } = this.props;
    return (
      <div className="app-dialog app-dialog--success photo-gate">
        <div className="app-dialog-badge" aria-hidden="true">
          <CheckCircleOutlineIcon />
        </div>
        <h2 className="app-dialog-title" id="photo-gate-title">
          All photos uploaded
        </h2>
        <p className="app-dialog-text">You&rsquo;re good to go.</p>
        <div className="app-dialog-buttons">
          <button
            type="button"
            className="app-dialog-btn app-dialog-btn--ghost"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="app-dialog-btn app-dialog-btn--primary"
            onClick={onProceed}
          >
            {actionLabel}
          </button>
        </div>
      </div>
    );
  }

  renderWaiting(status) {
    const { onCancel } = this.props;
    const { checking } = this.state;
    const count = status.count;
    const one = count === 1;
    const here = status.onThisDevice > 0;
    const queue = this.props.bonusPhotoQueue;
    const pct =
      queue && queue.uploading && queue.progress ? queue.progress.pct : null;
    const stalled = here && status.stalled > 0;

    return (
      <div
        className={`app-dialog photo-gate ${
          stalled
            ? 'app-dialog--warning photo-gate--stalled'
            : 'photo-gate--uploading'
        }`}
      >
        <div className="app-dialog-badge" aria-hidden="true">
          {stalled ? <CloudOffIcon /> : <PhoneIphoneIcon />}
        </div>

        <h2 className="app-dialog-title" id="photo-gate-title">
          {count} bonus {one ? 'photo' : 'photos'} not sent
        </h2>

        <p className="app-dialog-text">
          {here
            ? `Keep this app open until ${one ? 'it uploads' : 'they upload'}.`
            : `Open the NuVe app on the phone and wait until ${
                one ? 'it uploads' : 'they upload'
              }.`}
        </p>

        {/* Determinate whenever axios is reporting bytes for a photo actually
            in flight; indeterminate otherwise, because a bar that invents a
            percentage is worse than one that admits it doesn't know. */}
        {typeof pct === 'number' ? (
          <>
            <div
              className="photo-gate-meter photo-gate-meter--determinate"
              aria-hidden="true"
            >
              <span style={{ width: `${pct}%` }} />
            </div>
            <p className="photo-gate-status">Uploading… {pct}%</p>
          </>
        ) : (
          <div className="photo-gate-meter" aria-hidden="true">
            <span />
          </div>
        )}

        <div className="app-dialog-buttons">
          <button
            type="button"
            className="app-dialog-btn app-dialog-btn--ghost"
            onClick={this.handleRetry}
            disabled={checking}
          >
            {checking ? 'Checking…' : here ? 'Try now' : 'Check again'}
          </button>
          <button
            type="button"
            className="app-dialog-btn app-dialog-btn--primary"
            onClick={onCancel}
          >
            OK
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { open, onCancel } = this.props;
    const { status } = this.state;
    return (
      <Dialog
        open={open}
        onClose={onCancel}
        aria-labelledby="photo-gate-title"
        classes={{ paper: 'app-dialog-paper photo-gate-paper' }}
      >
        {status && status.count === 0
          ? this.renderClear()
          : this.renderWaiting(
              status || { count: 0, onThisDevice: 0, stalled: 0 }
            )}
      </Dialog>
    );
  }
}

PendingPhotoGate.propTypes = {
  open: PropTypes.bool,
  // What the employee was trying to do, e.g. 'Clock Out' / 'Log Out'.
  actionLabel: PropTypes.string,
  onCancel: PropTypes.func,
  // Run only once nothing is outstanding. There is no other way through.
  onProceed: PropTypes.func,
};

PendingPhotoGate.defaultProps = {
  open: false,
  actionLabel: 'Continue',
  onCancel: () => {},
  onProceed: () => {},
};

const mapStateToProps = (state) => ({
  user: userInfoSelector(state),
  bonusPhotoQueue: bonusPhotoQueueSelector(state),
});

const actionCreators = {
  drainBonusPhotos,
  refreshBonusPhotoQueue,
};

export default withShipment(
  { actionCreators, mapStateToProps },
  PendingPhotoGate
);
